import { rgbToHex } from '@mui/material/styles';
import { blend } from '@mui/system/colorManipulator';

import { parseHMS, TimeSpan } from '@/utils/datetime';

/**
 * How long a run occupies the schedule, in milliseconds: its estimate plus its setup time.
 *
 * TODO: A run's actual duration (start and end times recorded by OES' timer) used to take over from the
 * estimate whenever both were set, which is why the editor's start times drifted away from the
 * public schedule's. Disabled for now: there's no way to visualize that offset, no way to offset
 * it back, and no way to correct bad times coming out of the timer. Re-enable the branch below
 * once the editor can show and fix those.
 */
export function calculateRunSlotDuration(run) {
  // choose actual duration or fallback to estimate
  // const durationMs =
  //   run.startedAt && run.endedAt
  //     ? new Date(run.endedAt).getTime() - new Date(run.startedAt).getTime()
  //     : parseHMS(run.estimate) * 1000;
  const durationMs = parseHMS(run.estimate) * 1000;

  // add setupTime (in minutes) if present
  const setupMs = (run.setupTime ?? 0) * 60_000;

  return durationMs + setupMs;
}

/**
 * Default current run color: MUI's own gray dropdown highlight, flattened onto the paper so it's
 * opaque and contrastable, as a hex code since that's all the native color input takes.
 */
export const getDefaultCurrentRunColor = theme =>
  rgbToHex(
    blend(
      theme.palette.background.paper,
      // MUI tints with white in dark mode and black in light mode
      theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
      theme.palette.action.selectedOpacity,
    ),
  );

/**
 * Color of the public schedule's current run, and of the schedule editor's row hover.
 * The editor is a preview of the public schedule, so both sides share the one color.
 */
export const getCurrentRunColor = (schedule, theme) =>
  schedule?.currentRunColor || getDefaultCurrentRunColor(theme);

/** What day separators look like on a schedule that never picked a color of its own */
export const getDefaultDaySeparatorColor = theme => theme.palette.primary.main;

/** Color of the public schedule's day headers and of the schedule editor's separator rows */
export const getDaySeparatorColor = (schedule, theme) =>
  schedule?.daySeparatorColor || getDefaultDaySeparatorColor(theme);

/**
 * When each run is scheduled to start, keyed by run id.
 * Runs are back to back, so each one starts where the previous one's slot ended.
 */
export function calculateScheduledStartTimes(runs, event) {
  /** @type {Record<number, Date>} */
  const startTimes = {};
  if (!event) return startTimes;

  // beginning of schedule
  let cursor = new Date(event.startAt);

  for (const run of runs ?? []) {
    startTimes[run.id] = cursor;
    cursor = new Date(cursor.getTime() + calculateRunSlotDuration(run));
  }

  return startTimes;
}

/**
 * HACK: This is very hacky, VGR said we could use the ticker. This was patched in during NR7
 *
 * The run playing right now: the last one whose scheduled start time has passed.
 * Returns `null` before the schedule has started.
 *
 * @param runs The runs, in schedule order
 * @param getStartTime Reads a run's scheduled start time, which each page computes its own way
 */
export function findCurrentRunId(runs, getStartTime) {
  const now = Date.now();
  let currentRunId = null;
  for (const run of runs ?? []) {
    const startTime = getStartTime(run);
    if (!startTime || startTime.getTime() > now) break;
    currentRunId = run.id;
  }

  return currentRunId;
}

/**
 * Total time each host is assigned to, per "block" of consecutive runs sharing that host.
 * Keyed by run id, so every run of a block maps to its block's `total` (formatted as HH:MM:SS)
 * to the `blockId` it belongs to, and to the ids of every run in that block.
 * Runs without a host are left out.
 *
 * A block deliberately spans day boundaries: a host sitting through midnight is still
 * a single stretch of assigned time, no matter how the schedule is split up visually.
 */
export function calculateHostBlockTimes(runs) {
  /** @type {Record<number, {blockId: number, runIds: number[], total: string}>} */
  const blockTimes = {};
  let blockStart = 0;
  while (blockStart < (runs?.length ?? 0)) {
    let span = 1;
    while (
      blockStart + span < runs.length
      && runs[blockStart + span].host === runs[blockStart].host
    ) {
      span += 1;
    }
    const block = runs.slice(blockStart, blockStart + span);
    if (runs[blockStart].host) {
      const totalMs = block.reduce((total, run) => total + calculateRunSlotDuration(run), 0);
      const blockTime = {
        blockId: runs[blockStart].id,
        runIds: block.map(run => run.id),
        total: new TimeSpan(totalMs).format(),
      };
      for (const run of block) blockTimes[run.id] = blockTime;
    }
    blockStart += span;
  }

  return blockTimes;
}

/**
 * @param {object} run
 * @param {string} [run.startedAt]
 * @param {string} [run.endedAt]
 */
export const calculateRunTime = ({ startedAt, endedAt }) => {
  if (!startedAt || !endedAt) return '';
  const spanInMs = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (spanInMs < 0) return 'INVALID (start before end)';

  return new TimeSpan(spanInMs).format({ milliseconds: true });
};
